import { makeAutoObservable, reaction } from 'mobx'

import {
  type Carriers,
  type DropDown,
  type ObjectProperties
} from '../Pages/MapPage/Monitor/@types'
import { apiCall } from '../Api'
import { type Report } from '../Pages/MapPage/@types'
import type AppStore from './AppStore'
import { FeatureType, UserRole } from './features'

const convert = require('convert-units')

class MonitorStore {
  // public monitorObject: any
  public editMode = false
  public dropDown: DropDown[] = []
  public monitorOpen: boolean = false
  public objectResponse: any
  public showDeleteMessage: boolean = false
  public editObjectProperties?: ObjectProperties
  public simulatedObjectProperties: ObjectProperties[] = []
  public monitorObjectCarriers: Carriers[] = []
  public printFormValue = {
    title: '',
    datasource: '',
    author: '',
    date: '',
    logo: '',
    legend: false,
    scale: false,
    northarrow: false
  }

  public printMode: boolean = false
  public availableReports: Report[] | any = []
  public availableForms: Report[] | any = []
  public modalReportOpen: boolean = false
  public modalFormOpen: boolean = false
  public modalPrintOpen: boolean = false
  public isSaving: boolean = false
  private readonly appStore: AppStore

  public editedQuarterTitle: string = ''

  public disctrictheatingIds: number[] = [3, 15, 16, 17, 18, 19]

  constructor(appStore: AppStore) {
    makeAutoObservable(this)
    this.appStore = appStore

    reaction(
      () => appStore.featureFocus,
      () => {
        this.openMonitor()
      }
    )

    if (appStore.userRole === UserRole.Public) return

    reaction(
      () => JSON.stringify(this.appStore.featureFocus.formPlaceHolder ?? {}),
      () => {
        this.editObjectProperties = {
          ...this.appStore.featureFocus.formPlaceHolder
        }
      }
    )

    reaction(
      () => [
        this.editMode,
        this.appStore.viewStore.monitorVariablesChanged,
        this.appStore.focusType
      ],
      () => {
        if (!this.editMode || !this.appStore.viewStore.monitorVariablesChanged)
          this.appStore.viewStore.setFeatureChangeOnEdit(false)
        if (this.editMode && this.appStore.viewStore.monitorVariablesChanged) {
          this.appStore.viewStore.setShowModalRequest(true)
          this.appStore.viewStore.setIsEditWarning(true)
          this.appStore.viewStore.setShowEditWarning(false)
        } else if (
          [FeatureType.Quarter, FeatureType.Filter].includes(
            this.appStore.focusType
          ) &&
          this.editMode
        ) {
          this.appStore.viewStore.setShowModalRequest(true)
          this.appStore.viewStore.setIsEditWarning(false)
          this.appStore.viewStore.setShowEditWarning(true)
        } else {
          this.appStore.viewStore.setShowModalRequest(false)
          this.appStore.viewStore.setIsEditWarning(false)
          this.appStore.viewStore.setShowEditWarning(false)
        }
      }
    )
  }

  get monitorObject() {
    return {
      ...this.appStore.featureFocus.monitorData,
      type: this.appStore.focusType,
      layerType: this.appStore.focusType,
      ids: this.appStore.focusIds
    }
  }

  async editBackendData(fields) {
    this.objectResponse = {}

    const editProps = this.editObjectProperties
    const tmpProps = this.appStore.featureFocus.formPlaceHolder ?? {}
    const keys = fields
      .map((el) => el.patchapivariable)
      .filter((val) => Object.keys(editProps).includes(val))
      .filter(
        (key) =>
          key !== 'uuid' &&
          key !== 'oid' &&
          key !== 'geometry' &&
          (isNaN(editProps[key])
            ? editProps[key] !== tmpProps[key]
            : Math.abs(editProps[key] - tmpProps[key]) / (editProps[key] || 0) >
              1e-4) // deviation < 0.01%
      )

    const postContent = {}
    for (let i = 0; i < keys.length; i++) {
      postContent[keys[i]] = editProps[keys[i]]
    }

    if (
      this.appStore.focusType === FeatureType.Quarter &&
      this.appStore.featureFocus.title !== this.editedQuarterTitle &&
      this.editedQuarterTitle !== ''
    ) {
      await this.appStore.featureFocus
        ?.patch({ title: this.editedQuarterTitle })
        .catch((e) => {
          return e.response
        })
      if (keys.length === 0) {
        return this.objectResponse
      }
    }

    if ('buildings' in this.appStore.featureFocus) {
      return await this.appStore.featureFocus.buildings
        .patch(postContent)
        .then((res) => (this.objectResponse = res))
    }

    if (this.appStore.focusType === FeatureType.EnergyPlant) {
      const uuid = this.appStore.featureFocus.uuid

      return await apiCall({
        url:
          '/api/v1/project/' +
          this.appStore.appStore.selectedProject.uuid +
          '/energyplant/' +
          uuid +
          '/source/',
        method: 'GET',
        accessToken: this.appStore.userStore.token?.access
      }).then(async (res) => {
        // TODO: Instead we should use the react hook form tools in further iterations
        const sourceContent = {
          unit: this.editObjectProperties?.energyCarrierUnit,
          carrier: this.editObjectProperties?.energyCarrier,
          amount: this.editObjectProperties?.amount
        }

        await apiCall({
          url:
            '/api/v1/project/' +
            this.appStore.appStore.selectedProject.uuid +
            '/energyplant/' +
            uuid +
            '/source/' +
            res[0].uuid +
            '/',
          body: JSON.stringify(sourceContent),
          responseType: 'raw',
          method: 'PATCH',
          accessToken: this.appStore.userStore.token?.access
        }).then((res) => {
          return res
        })

        const energyPlantContent = {
          plant_type: this.editObjectProperties?.plant_type,
          carrier: this.editObjectProperties?.energyCarrier,
          unit: this.editObjectProperties?.energyCarrierUnit,
          output_heat: this.editObjectProperties?.output_heat,
          output_power: this.editObjectProperties?.output_power,
          full_load_hours: this.editObjectProperties?.full_load_hours,
          grid_connection_heat: this.editObjectProperties?.grid_connection_heat,
          grid_connection_power:
            this.editObjectProperties?.grid_connection_power
        }

        return await apiCall({
          url:
            '/api/v1/project/' +
            this.appStore.appStore.selectedProject.uuid +
            '/energyplant/' +
            uuid +
            '/',
          body: JSON.stringify(energyPlantContent),
          responseType: 'raw',
          method: 'PATCH',
          accessToken: this.appStore.userStore.token?.access
        }).then((res) => {
          return res
        })
      })
    }

    await this.appStore.featureFocus
      .patch(postContent)
      .then((res) => (this.objectResponse = res))

    return this.objectResponse
  }

  openMonitor() {
    this.monitorOpen = true
  }

  closeMonitor() {
    this.monitorOpen = false
  }

  addDropDown(dropDown: DropDown) {
    this.dropDown.push(dropDown)
  }

  // Toggle and Visibility
  toggleDeleteMessage() {
    this.showDeleteMessage = !this.showDeleteMessage
  }

  toggleEditMode() {
    this.dropDown = []
    this.setEditMode(!this.editMode)
  }

  showReportModal = (res) => {
    this.availableReports = res
    this.setModalReportOpen(true)
    this.setModalFormOpen(false)
    this.setModalPrintOpen(false)
  }

  showFormModal = (res) => {
    this.availableForms = res
    this.setModalReportOpen(false)
    this.setModalFormOpen(true)
    this.setModalPrintOpen(false)
  }

  getDependingDropdownData(apivariable: string) {
    switch (apivariable) {
      case 'energycarrier':
        return this.appStore.getCarrierUnits(
          Number.parseInt(this.editObjectProperties?.energyCarrier) ?? -1
        )
    }
  }

  getConvertPossibilitiesIndex(unit: string) {
    return convert()
      .from(unit)
      .possibilities()
      .findIndex((poss: string) => unit === poss)
  }

  getDropdownData = (
    apivariable: string,
    label: string,
    patchapivariable: string,
    uuid: string,
    depends?: string
  ) => {
    if (patchapivariable === 'heatsupply') {
      return this.appStore.heatSuppliesList
    }
    if (depends) {
      return this.getDependingDropdownData(apivariable)
    }

    return this.dropDown?.find(
      (drop) => drop.name === label + '-' + apivariable
    )?.values
  }

  async setEditObjectProperties(objectType: string, id: string) {
    this.editObjectProperties = {
      ...this.appStore.featureFocus.formPlaceHolder
    }
    return this.editObjectProperties
  }

  setPrintFormValue(key, value): void {
    this.printFormValue[key] = value
  }

  setPrintMode(printMode): void {
    this.printMode = printMode
  }

  setModalFormOpen(modalFormOpen): void {
    this.modalFormOpen = modalFormOpen
  }

  setModalReportOpen(modalReportOpen): void {
    this.modalReportOpen = modalReportOpen
  }

  setModalPrintOpen(modalPrintOpen): void {
    this.modalPrintOpen = modalPrintOpen
  }

  setEditMode(editMode: boolean): void {
    this.editMode = editMode
  }

  get isLoading(): boolean {
    return this.appStore.featureFocus.monitorData === undefined
  }

  setIsSaving(isSaving: boolean): void {
    this.isSaving = isSaving
  }

  setEditedQuarterTitle(editedQuarterTitle: string): void {
    this.editedQuarterTitle = editedQuarterTitle
  }
}

export default MonitorStore
